import * as THREE from 'three';
import { ATMO, INTERCEPTOR } from '../config/tuning';
import type { Checksum, Checksummable } from '../core/Checksum';
import type { Terrain } from '../world/Terrain';
import { proportionalNavigation, limitAcceleration } from './Guidance';
import type { RadarTarget } from './Detection';

/**
 * Interceptor drone (SPEC §3.5.1, and the M7 note): pursuit with proportional navigation, and —
 * the part that matters — *energy-aware*.
 *
 * The M7 acceptance criterion is that the interceptor "can be defeated by an energy manoeuvre",
 * which means it must be willing to lose. An interceptor that pulled its g limit regardless would
 * be either unbeatable or stupid; one that refuses to follow into a stall can be *made* to
 * overshoot, and being made to overshoot is a skill the player can learn and repeat.
 *
 * So it flies with three rules, in priority order:
 *
 *  1. **Do not stall.** Below the corner speed it trades commanded g for airspeed, exactly as a
 *     pilot would. This is the rule the player exploits.
 *  2. **Do not hit the ground.** Terrain avoidance overrides pursuit; chasing a drone into a
 *     valley floor is how interceptors are lost.
 *  3. **Pursue**, by proportional navigation, with a lead that tightens as it closes.
 *
 * Units: metres, m/s, m/s², radians, seconds.
 */

export const InterceptorState = {
  /** Cruising toward its patrol point, not yet committed. */
  Patrol: 'patrol',
  /** Committed to the target. */
  Pursuit: 'pursuit',
  /** Too slow to manoeuvre: unloading to regain energy. The window the player made. */
  Recovering: 'recovering',
  /** Overshot and is repositioning for another pass. */
  Reattack: 'reattack',
  Dead: 'dead',
} as const;
export type InterceptorStateId = (typeof InterceptorState)[keyof typeof InterceptorState];

const relativePosition = new THREE.Vector3();
const relativeVelocity = new THREE.Vector3();
const command = new THREE.Vector3();
const acceleration = new THREE.Vector3();
const forwardScratch = new THREE.Vector3();
const avoidScratch = new THREE.Vector3();

export class Interceptor implements Checksummable {
  readonly positionM = new THREE.Vector3();
  readonly velocityMs = new THREE.Vector3();
  readonly previousPositionM = new THREE.Vector3();

  designation = 'I-1';
  state: InterceptorStateId = InterceptorState.Patrol;
  active = false;

  /** Seconds spent recovering energy — the window an energy manoeuvre buys the player. */
  recoveringS = 0;
  /** Range at the last closest approach, metres. Used to detect an overshoot. */
  private lastRangeM = Number.POSITIVE_INFINITY;

  spawn(positionM: THREE.Vector3, headingRad: number): void {
    this.positionM.copy(positionM);
    this.previousPositionM.copy(positionM);
    this.velocityMs.set(
      Math.sin(headingRad) * INTERCEPTOR.cruiseSpeedMs,
      0,
      -Math.cos(headingRad) * INTERCEPTOR.cruiseSpeedMs,
    );
    this.state = InterceptorState.Patrol;
    this.recoveringS = 0;
    this.lastRangeM = Number.POSITIVE_INFINITY;
    this.active = true;
  }

  get speedMs(): number {
    return this.velocityMs.length();
  }

  /** Specific energy, J/kg — the currency the whole engagement is fought in (SPEC §3.6.5). */
  get specificEnergyJKg(): number {
    return 0.5 * this.velocityMs.lengthSq() + ATMO.G * this.positionM.y;
  }

  kill(): void {
    this.state = InterceptorState.Dead;
    this.active = false;
  }

  /**
   * One tick of pursuit. Returns the range to the target, metres.
   *
   * Step 6 of the tick order (SPEC §4.3), after missiles and before detection.
   */
  tick(dtS: number, target: RadarTarget, terrain: Terrain): number {
    if (!this.active || this.state === InterceptorState.Dead) return Number.POSITIVE_INFINITY;

    this.previousPositionM.copy(this.positionM);

    relativePosition.copy(target.positionM).sub(this.positionM);
    relativeVelocity.copy(target.velocityMs).sub(this.velocityMs);
    const rangeM = relativePosition.length();

    this.updateState(rangeM, dtS);

    acceleration.set(0, -ATMO.G, 0);

    if (this.state === InterceptorState.Recovering) {
      // Unload and accelerate: nose down a little, engine at full. The classic energy recovery,
      // and while it lasts the interceptor is not pointing at anybody.
      forwardScratch.copy(this.velocityMs).normalize();
      acceleration.addScaledVector(forwardScratch, INTERCEPTOR.thrustMs2);
      acceleration.y -= INTERCEPTOR.recoverDescentMs2;
    } else {
      // Pursuit. PN with the same law the missiles use — one guidance implementation, one set of
      // behaviours to learn.
      proportionalNavigation(
        relativePosition,
        relativeVelocity,
        this.velocityMs,
        INTERCEPTOR.navConstant,
        command,
      );
      command.y += ATMO.G;

      // Rule 1: never command more than the airframe can hold at this speed. This is where an
      // energy-starved interceptor stops being able to follow.
      limitAcceleration(command, this.availableG());
      acceleration.add(command);

      // Thrust toward the commanded speed.
      forwardScratch.copy(this.velocityMs).normalize();
      const speedError = INTERCEPTOR.cruiseSpeedMs - this.speedMs;
      acceleration.addScaledVector(
        forwardScratch,
        Math.max(-INTERCEPTOR.thrustMs2, Math.min(INTERCEPTOR.thrustMs2, speedError * 0.6)),
      );
    }

    // Rule 2: the ground wins every argument.
    this.avoidTerrain(terrain);

    // Drag: the reason a turning interceptor bleeds and the reason recovery takes time.
    const speed = this.speedMs;
    if (speed > 1e-6) {
      const dragMs2 = INTERCEPTOR.dragPerSpeedSq * speed * speed;
      acceleration.addScaledVector(this.velocityMs, -dragMs2 / speed);
    }

    this.velocityMs.addScaledVector(acceleration, dtS);
    this.positionM.addScaledVector(this.velocityMs, dtS);

    // Never let it fly through the world.
    const groundM = terrain.surfaceHeightAt(this.positionM.x, this.positionM.z);
    if (this.positionM.y < groundM + 2) {
      this.kill();
    }

    this.lastRangeM = rangeM;
    return rangeM;
  }

  checksum(into: Checksum): void {
    into.addFloat(this.positionM.x);
    into.addFloat(this.positionM.y);
    into.addFloat(this.positionM.z);
    into.addFloat(this.velocityMs.x);
    into.addFloat(this.velocityMs.y);
    into.addFloat(this.velocityMs.z);
    into.addFloat(this.recoveringS);
  }

  /**
   * G available at the current speed.
   *
   * Below the corner speed the wing simply cannot generate the limit load, so the available g
   * falls with the square of airspeed — the same physics that governs the player's own airframe.
   * This function is the entire reason an energy manoeuvre works: drag the interceptor down to
   * 60% of its corner speed and it has barely a third of its turn left.
   */
  availableG(): number {
    const ratio = this.speedMs / INTERCEPTOR.cornerSpeedMs;
    const limited = INTERCEPTOR.maxG * Math.min(1, ratio * ratio);
    return Math.max(INTERCEPTOR.minG, limited);
  }

  private updateState(rangeM: number, dtS: number): void {
    if (this.state === InterceptorState.Recovering) {
      this.recoveringS += dtS;
      // Recovered once it is back above the speed where it can fight again.
      if (this.speedMs >= INTERCEPTOR.recoverToSpeedMs) {
        this.state = InterceptorState.Pursuit;
        this.recoveringS = 0;
      }
      return;
    }

    // Rule 1, the decision itself: an interceptor that would have to stall to stay in the turn
    // gives up the turn instead. SPEC's M7 note requires exactly this — "will not follow into a
    // stall" — and it is what converts a hard, slow, descending spiral into a survivable one.
    if (this.speedMs <= INTERCEPTOR.stallGuardSpeedMs) {
      this.state = InterceptorState.Recovering;
      this.recoveringS = 0;
      return;
    }

    if (rangeM > INTERCEPTOR.commitRangeM) {
      this.state = InterceptorState.Patrol;
      return;
    }

    // Overshoot: the range bottomed out and is opening again inside the merge.
    if (rangeM < INTERCEPTOR.mergeRangeM && rangeM > this.lastRangeM) {
      this.state = InterceptorState.Reattack;
      return;
    }

    this.state = InterceptorState.Pursuit;
  }

  /** Pull up if the ground is close, hard enough to override whatever pursuit wanted. */
  private avoidTerrain(terrain: Terrain): void {
    const groundM = terrain.surfaceHeightAt(this.positionM.x, this.positionM.z);
    const clearanceM = this.positionM.y - groundM;
    if (clearanceM > INTERCEPTOR.terrainClearanceM) return;

    const urgency = 1 - clearanceM / INTERCEPTOR.terrainClearanceM;
    avoidScratch.set(0, INTERCEPTOR.maxG * ATMO.G * urgency, 0);
    acceleration.add(avoidScratch);
  }
}
